/**
 * useCollectionMetrics Hook
 *
 * React Query hook for fetching collection-level metrics with 24h rolling window.
 * Follows the useTodayMetrics pattern.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchWithAuth } from '../../../../utils/api';
import { APIErrorException } from '../../../../services/types/errors';
import type { CollectionMetricsResponse } from '../types/collectionMetrics.types';

// =============================================================================
// Query Keys
// =============================================================================

export const collectionMetricsKeys = {
  all: ['collection-metrics'] as const,
  metrics: (collectionId: string) =>
    [...collectionMetricsKeys.all, collectionId] as const,
};

// =============================================================================
// Constants
// =============================================================================

const STALE_TIME = 30_000; // 30 seconds
const REFETCH_INTERVAL = 60_000; // 60 seconds

// =============================================================================
// Hook
// =============================================================================

export interface UseCollectionMetricsResult {
  data: CollectionMetricsResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useCollectionMetrics(
  collectionId: string
): UseCollectionMetricsResult {
  const { data, isLoading, isError, error, refetch } = useQuery<
    CollectionMetricsResponse,
    Error
  >({
    queryKey: collectionMetricsKeys.metrics(collectionId),
    queryFn: async () => {
      return fetchWithAuth(`/v1/collections/${collectionId}/metrics`);
    },
    enabled: !!collectionId,
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (error instanceof APIErrorException && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    data: data ?? null,
    isLoading,
    isError,
    error: error ?? null,
    refetch,
  };
}
